import React from 'react';
import DynamicSelect from '@ui-library/dynamicSelect';
import { apiAuthCaller } from '@services/apiCaller';
import useStores from '@stores/useStores';
import { SmartRetry } from '@utils/retry';
import axios from 'axios';
import classes from '@components/sources/visualizer/mappingScreens/config/mappingConfig/mapping-commons.module.scss';
import { reloadButton } from '@components/sources/visualizer/mappingScreens/config/mappingConfig/SelectUniqueIdentifier';
import {
  fetchErrorAssets,
  ErrorProps,
} from '@components/sources/visualizer/mappingScreens/errorsView';
import { getApiErrorMessage } from '@components/common/util/util';
import { FixMe } from '@utils/types';
import { ConfigEditorProps } from '.';
import { warehouseList } from '../../warehouseSourceList/warehouseSettings';
import { get } from 'lodash';

export interface TableConfig {
  schema?: string;
  table?: string;
}

interface ConfigureTableProps extends ConfigEditorProps<TableConfig> {
  role: string;
  config: Partial<TableConfig>;
  showRequired?: boolean;
  showReload?: boolean;
  updateSchemaErrors?: (errorItems: ErrorProps[]) => void;
}

const retry = new SmartRetry<FixMe>({ times: 1 });
const TableSelector: React.FC<ConfigureTableProps> = ({
  defaultValue,
  showReload = true,
  showRequired = true,
  onChangeConfig,
  accountId,
  onChangeColumns,
  config,
  role,
  onChangeLoadingState,
  isViewOrUpdate,
  columnsAsObject,
  mappedToDestination,
  updateSchemaErrors,
}: ConfigureTableProps) => {
  const schemaAlias: string =
    get(warehouseList, role.toUpperCase(), [{}])[0]?.schemaAlias || 'Schema';
  const schemaAliasName = `${schemaAlias.toLowerCase()}s`;
  const reloadButtonText = `${schemaAliasName} and tables`;
  const timeout = 120000;
  const [loadingCols, setLoadingCols] = React.useState(false);

  const [refresh, setRefresh] = React.useState({ schema: false, table: false });
  const { userStore, messagesStore } = useStores();
  React.useEffect(() => {
    if (onChangeLoadingState) {
      onChangeLoadingState(loadingCols);
    }
  }, [loadingCols, refresh]);

  const startReload = () => {
    if (updateSchemaErrors)
      updateSchemaErrors([fetchErrorAssets(schemaAliasName), fetchErrorAssets('tables')]);

    setRefresh({ schema: true, table: false });
  };
  const refreshSchemaAndTablesButton = () => (
    <div className={classes.dropdown_reload_button_wrapper}>
      {reloadButton(
        { type: 'text', refreshing: refresh.schema || refresh.table, onClick: startReload },
        reloadButtonText,
      )}
    </div>
  );

  return (
    <div className="sql_container">
      <DynamicSelect
        field={{
          method: 'post',
          timeout,
          label: schemaAlias,
          required: showRequired,
          defaultValue: defaultValue?.schema || '',
          params: {
            accountId,
          },
          showSearch: true,
          responseKey: 'schemas',
          labelKey: 'name',
          valueKey: 'name',
          url: `cloudSources/sources/info/${role}/schemas`,
          value: config?.schema || '',
        }}
        onChange={(_, val) => {
          onChangeConfig({ schema: val });
          if (updateSchemaErrors)
            updateSchemaErrors([
              fetchErrorAssets('tables'),
              fetchErrorAssets('columns'),
              fetchErrorAssets('preview'),
            ]);
        }}
        onError={(err) => {
          const errorMsg = getApiErrorMessage(err, `Failed to fetch ${schemaAliasName}`);
          messagesStore.showErrorMessage(errorMsg);
          if (updateSchemaErrors)
            updateSchemaErrors([fetchErrorAssets(schemaAliasName, errorMsg, reloadButtonText)]);
        }}
        disabled={isViewOrUpdate}
        refresh={refresh.schema}
        setRefresh={(val: boolean, valueIsInvalid?: boolean) =>
          setRefresh({ schema: val, table: !valueIsInvalid && Boolean(config.schema) })
        }
      />
      <div style={{ marginTop: 24 }} />
      <DynamicSelect
        showSearch
        loading={loadingCols || refresh.schema}
        preRequisiteValue={config?.schema}
        disabled={!config.schema || isViewOrUpdate}
        refresh={refresh.table}
        setRefresh={(val: boolean) => setRefresh({ schema: false, table: val })}
        field={{
          method: 'post',
          timeout,
          label: 'Table',
          required: showRequired,
          showSearch: true,
          url: `cloudSources/sources/info/${role}/tables`,
          defaultValue: defaultValue?.table || '',
          value: config?.table || '',
          params: {
            accountId,
            schema: config.schema,
          },
          responseKey: 'tables',
          labelKey: 'name',
          valueKey: 'name',
        }}
        onError={(err) => {
          const errorMsg = getApiErrorMessage(err, 'Failed to fetch tables');
          messagesStore.showErrorMessage(errorMsg);
          if (updateSchemaErrors)
            updateSchemaErrors([fetchErrorAssets('tables', errorMsg, reloadButtonText)]);
        }}
        onChange={(_, table) => {
          onChangeConfig({ table });
          if (isViewOrUpdate && mappedToDestination) {
            return;
          }
          if (updateSchemaErrors)
            updateSchemaErrors([fetchErrorAssets('columns'), fetchErrorAssets('preview')]);
          if (table) {
            setLoadingCols(true);
            retry
              .exec(() =>
                apiAuthCaller(userStore.token, false, timeout).post(
                  `cloudSources/sources/info/${role}/columns`,
                  {
                    accountId,
                    schema: config.schema,
                    table,
                  },
                  {
                    cancelToken: new axios.CancelToken((c) => {
                      retry.setCancelMethod(c);
                    }),
                  },
                ),
              )
              .then((resp) => {
                const columns = columnsAsObject
                  ? resp.data?.columns
                  : resp?.data?.columns?.map((col: { name: string }) => col.name);
                if (!columns?.length) {
                  throw {
                    message: `No columns found in table ${config.table}`,
                  };
                }

                onChangeColumns(columns);
              })
              .catch((err) => {
                if (err.constructor.name === 'Cancel') {
                  return;
                }
                onChangeColumns([]);
                const errorMsg = getApiErrorMessage(err, 'Failed to fetch columns');
                messagesStore.showErrorMessage(errorMsg);
                if (updateSchemaErrors) updateSchemaErrors([fetchErrorAssets('columns', errorMsg)]);
              })
              .finally(() => {
                setLoadingCols(false);
              });
          } else {
            onChangeColumns([]);
          }
        }}
      />
      {!isViewOrUpdate && showReload && refreshSchemaAndTablesButton()}
      {!mappedToDestination && <div style={{ marginBottom: 24 }} />}
    </div>
  );
};

export default TableSelector;
